import { useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../reducers';
import { AsyncDispatch } from '../../types/global';
import { useAppDispatch } from '../store/ReduxStore';
import { fetchReplitIntegrations } from '../actions/replit';
import { fetchCustomAppConfigs } from '../actions/customApps';
import { hasPermission } from '../utils/Permissions';
import { CustomAppLevel } from '../../types/customApps';
import { getCustomAppsForLevel, CustomAppWithDisplay } from '../utils/CustomAppsUtils';

export interface UseCustomAppsReturn {
  getAppsForLevel: (level: CustomAppLevel, entityId?: number) => CustomAppWithDisplay[];
}

/**
 * Hook to fetch and filter custom apps by level and entity.
 * Returns a function that can be called to get filtered apps for any level/entity combination.
 */
export const useCustomApps = (): UseCustomAppsReturn => {
  const dispatch: AsyncDispatch = useAppDispatch();

  const user = useSelector((state: ApplicationState) => state.user.user);
  const integrations = useSelector((state: ApplicationState) => state.tools.replit.integrations);
  const customAppConfigs = useSelector(
    (state: ApplicationState) => state.customAppsSettings.configs
  );

  const canAccessCustomApps = useMemo(() => hasPermission(user, 'can_access_replit'), [user]);

  useEffect(() => {
    if (canAccessCustomApps && integrations.length === 0) {
      dispatch(fetchReplitIntegrations());
    }
  }, [dispatch, canAccessCustomApps, integrations.length]);

  useEffect(() => {
    if (canAccessCustomApps && customAppConfigs.length === 0) {
      dispatch(fetchCustomAppConfigs());
    }
  }, [dispatch, canAccessCustomApps, customAppConfigs.length]);

  const getAppsForLevel = useCallback(
    (level: CustomAppLevel, entityId?: number): CustomAppWithDisplay[] => {
      if (!canAccessCustomApps) {
        return [];
      }
      return getCustomAppsForLevel(integrations, customAppConfigs, level, entityId);
    },
    [canAccessCustomApps, integrations, customAppConfigs]
  );

  return { getAppsForLevel };
};
